import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { admin, messageErreur } from '../services/api';
import { ROLES, libelle } from '../constants';
import { Chargement, Erreur, Modale, useReferentiels } from '../components/communs';
import { horodatage, nomComplet } from '../utils/format';

const ONGLETS = [
  ['concessions', 'Concessions'],
  ['clients', 'Clients'],
  ['profils', 'Profils'],
  ['integrations', 'Intégrations'],
];

// Paramètres — même gabarit que ceux d'A97 Hub : onglets dans l'URL,
// carte de création au-dessus du tableau, modale « Modifier ».
export default function Parametres({ moi }) {
  const [params, setParams] = useSearchParams();
  const demande = params.get('onglet');
  const onglet = ONGLETS.some(([k]) => k === demande) ? demande : 'concessions';
  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Paramètres</h1>
          <p>Concessions et clients auxquels rattacher les projets, profils des utilisateurs, liens avec le CRM et la messagerie.</p>
        </div>
        <Version />
      </div>
      <div className="page-tabs" role="tablist">
        {ONGLETS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={k === onglet} className={k === onglet ? 'actif' : ''} onClick={() => setParams({ onglet: k }, { replace: true })}>{l}</button>
        ))}
      </div>
      {onglet === 'concessions' && <Entreprises type="concession" />}
      {onglet === 'clients' && <Entreprises type="client" />}
      {onglet === 'profils' && <Profils moiId={moi.id} />}
      {onglet === 'integrations' && <Integrations />}
    </div>
  );
}

function Version() {
  const [etat, setEtat] = useState(null);
  useEffect(() => { admin.etat().then(setEtat).catch(() => setEtat(null)); }, []);
  if (!etat) return null;
  return (
    <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--texte-faible)' }}>
      <div>Version en service</div>
      <div className="mono">v{etat.version}</div>
    </div>
  );
}

// Bandeau « fiche reprise du CRM » : rappel que l'identité vient d'ailleurs.
const BadgeSource = ({ source }) => (source === 'crm'
  ? <span className="badge badge-succes badge-sans-point" title="Repris du CRM autodom97, tenu à jour par la synchronisation">CRM</span>
  : <span className="badge badge-neutre badge-sans-point" title="Créé dans A97 Projets">Manuel</span>);

const BadgeActif = ({ actif, oui = 'Active', non = 'Inactive' }) => (actif
  ? <span className="badge badge-succes badge-sans-point">{oui}</span>
  : <span className="badge badge-neutre badge-sans-point">{non}</span>);

// ─── Concessions et clients ──────────────────────────────────────────────────
function Entreprises({ type }) {
  const ref = useReferentiels();
  const client = type === 'client';
  const [liste, setListe] = useState(null);
  const [concessions, setConcessions] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [form, setForm] = useState({ nom: '', code: '', concession_id: '' });
  const [edition, setEdition] = useState(null);

  const charger = useCallback(() => Promise.all([admin.entreprises(type), client ? admin.entreprises('concession') : []])
    .then(([l, c]) => { setListe(l); setConcessions(c.filter((x) => x.actif)); })
    .catch((e) => setErreur(messageErreur(e))), [type, client]);
  useEffect(() => { setListe(null); setSucces(null); setErreur(null); charger(); }, [charger]);

  async function creer(e) {
    e.preventDefault();
    setErreur(null); setSucces(null);
    try {
      await admin.creerEntreprise({ ...form, type, concession_id: form.concession_id || null });
      setSucces(`${client ? 'Client' : 'Concession'} « ${form.nom} » ajouté${client ? '' : 'e'}.`);
      setForm({ nom: '', code: '', concession_id: '' });
      charger(); ref.recharger();
    } catch (err) {
      setErreur(messageErreur(err));
    }
  }

  const titre = client ? 'client' : 'concession';
  if (!liste) return erreur ? <Erreur onReessayer={charger}>{erreur}</Erreur> : <Chargement />;
  return (
    <>
      {erreur && <Erreur>{erreur}</Erreur>}
      {succes && <div className="succes-bandeau">{succes}</div>}
      <form className="carte" onSubmit={creer} style={{ marginBottom: 'var(--esp-4)' }}>
        <h2 className="carte-titre">{client ? 'Nouveau client' : 'Nouvelle concession'}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: client ? '2fr 1.5fr auto' : '2fr 1fr auto', gap: 'var(--esp-3)', alignItems: 'end' }} className="grille-formulaire">
          <div className="champ" style={{ marginBottom: 0 }}>
            <label htmlFor="ent-nom">Nom</label>
            <input id="ent-nom" required value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} placeholder={client ? 'Société cliente' : 'Nom de la concession'} />
          </div>
          {client ? (
            <div className="champ" style={{ marginBottom: 0 }}>
              <label htmlFor="ent-conc">Concession de rattachement (facultatif)</label>
              <select id="ent-conc" value={form.concession_id} onChange={(e) => setForm((f) => ({ ...f, concession_id: e.target.value }))}>
                <option value="">Aucune</option>
                {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
          ) : (
            <div className="champ" style={{ marginBottom: 0 }}>
              <label htmlFor="ent-code">Code (facultatif)</label>
              <input id="ent-code" className="mono" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="SOGUAVA" />
            </div>
          )}
          <button className="btn btn-primaire">Ajouter</button>
        </div>
      </form>
      {liste.length === 0 ? (
        <div className="vide carte">Aucun{client ? '' : 'e'} {titre} pour l’instant. Ajoutez-en ci-dessus, ou reprenez celles du CRM depuis l’onglet Intégrations.</div>
      ) : (
        <div className="table-container">
          <table className="table-cartes">
            <thead>
              <tr>
                <th>{client ? 'Client' : 'Concession'}</th>
                <th>{client ? 'Concession' : 'Code'}</th>
                <th>Origine</th>
                <th className="num">Projets</th>
                <th>État</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((x) => (
                <tr key={x.id}>
                  <td style={{ fontWeight: 550 }}>{x.nom}</td>
                  <td className={client ? '' : 'mono'}>{(client ? x.concession_nom : x.code) || '—'}</td>
                  <td><BadgeSource source={x.source} /></td>
                  <td className="num">{x.nb_projets}</td>
                  <td><BadgeActif actif={x.actif} oui={client ? 'Actif' : 'Active'} non={client ? 'Inactif' : 'Inactive'} /></td>
                  <td><button type="button" className="btn btn-petit" onClick={() => setEdition(x)}>Modifier</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edition && (
        <ModaleEntreprise
          entreprise={edition}
          concessions={concessions}
          onFermer={() => setEdition(null)}
          onFait={(m) => { setEdition(null); setSucces(m); charger(); ref.recharger(); }}
        />
      )}
    </>
  );
}

function ModaleEntreprise({ entreprise, concessions, onFermer, onFait }) {
  const crm = entreprise.source === 'crm';
  const client = entreprise.type === 'client';
  const [v, setV] = useState({ ...entreprise, code: entreprise.code || '', notes: entreprise.notes || '', concession_id: entreprise.concession_id || '' });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (p) => setV((x) => ({ ...x, ...p }));

  async function enregistrer() {
    setEnvoi(true); setErreur(null);
    try {
      await admin.modifierEntreprise(entreprise.id, { ...v, concession_id: v.concession_id || null });
      onFait('Enregistré.');
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  }
  async function supprimer() {
    if (!window.confirm(`Supprimer définitivement « ${entreprise.nom} » ?`)) return;
    try { await admin.supprimerEntreprise(entreprise.id); onFait('Supprimé.'); } catch (e) { setErreur(messageErreur(e)); }
  }

  return (
    <Modale
      titre={`${client ? 'Client' : 'Concession'} — ${entreprise.nom}`}
      onFermer={onFermer}
      actions={(
        <>
          {!crm && <button type="button" className="btn btn-danger" onClick={supprimer}>Supprimer</button>}
          <span style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button type="button" className="btn btn-primaire" onClick={enregistrer} disabled={envoi}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
        </>
      )}
    >
      {erreur && <Erreur>{erreur}</Erreur>}
      {crm && <div className="note-bandeau">Fiche reprise du CRM : le nom et le code y sont tenus. Ici, on règle les notes et l’état.</div>}
      <div className="champ">
        <label htmlFor="me-nom">Nom</label>
        <input id="me-nom" value={v.nom} disabled={crm} onChange={(e) => maj({ nom: e.target.value })} />
      </div>
      {client ? (
        <div className="champ">
          <label htmlFor="me-conc">Concession de rattachement</label>
          <select id="me-conc" value={v.concession_id} disabled={crm} onChange={(e) => maj({ concession_id: e.target.value })}>
            <option value="">Aucune</option>
            {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        </div>
      ) : (
        <div className="champ">
          <label htmlFor="me-code">Code</label>
          <input id="me-code" className="mono" value={v.code} disabled={crm} onChange={(e) => maj({ code: e.target.value })} />
        </div>
      )}
      <div className="champ">
        <label htmlFor="me-notes">Notes</label>
        <textarea id="me-notes" rows={3} value={v.notes} onChange={(e) => maj({ notes: e.target.value })} />
      </div>
      <div className="champ">
        <label className="bascule"><input type="checkbox" checked={v.actif} onChange={(e) => maj({ actif: e.target.checked })} />
          {client ? 'Actif' : 'Active'} (proposé{client ? '' : 'e'} lors du rattachement d’un projet)</label>
      </div>
    </Modale>
  );
}

// ─── Profils ─────────────────────────────────────────────────────────────────
function Profils({ moiId }) {
  const ref = useReferentiels();
  const [liste, setListe] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', role: 'membre' });
  const [edition, setEdition] = useState(null);

  const charger = useCallback(() => admin.habilitations().then(setListe).catch((e) => setErreur(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);

  async function creer(e) {
    e.preventDefault();
    setErreur(null); setSucces(null);
    try {
      await admin.creerHabilitation(form);
      setSucces(`Profil ${form.email} créé : la personne peut se connecter avec son compte Microsoft.`);
      setForm({ prenom: '', nom: '', email: '', role: 'membre' });
      charger(); ref.recharger();
    } catch (err) { setErreur(messageErreur(err)); }
  }

  if (!liste) return erreur ? <Erreur onReessayer={charger}>{erreur}</Erreur> : <Chargement />;
  const actifs = liste.filter((h) => h.actif).length;
  return (
    <>
      {erreur && <Erreur>{erreur}</Erreur>}
      {succes && <div className="succes-bandeau">{succes}</div>}
      <form className="carte" onSubmit={creer} style={{ marginBottom: 'var(--esp-4)' }}>
        <h2 className="carte-titre">Nouveau profil</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.6fr 1fr auto', gap: 'var(--esp-3)', alignItems: 'end' }} className="grille-formulaire">
          <div className="champ" style={{ marginBottom: 0 }}>
            <label htmlFor="u-prenom">Prénom</label>
            <input id="u-prenom" value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} />
          </div>
          <div className="champ" style={{ marginBottom: 0 }}>
            <label htmlFor="u-nom">Nom</label>
            <input id="u-nom" value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} />
          </div>
          <div className="champ" style={{ marginBottom: 0 }}>
            <label htmlFor="u-email">E-mail Microsoft</label>
            <input id="u-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="prenom.nom@groupe-aubery.com" />
          </div>
          <div className="champ" style={{ marginBottom: 0 }}>
            <label htmlFor="u-role">Rôle</label>
            <select id="u-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              {ROLES.map((r) => <option key={r.cle} value={r.cle}>{r.libelle}</option>)}
            </select>
          </div>
          <button className="btn btn-primaire">Créer</button>
        </div>
        <div className="aide faible" style={{ fontSize: 12, marginTop: 8 }}>
          Pas de mot de passe : la connexion se fait avec le compte Microsoft de l’adresse saisie.
          Les utilisateurs du CRM arrivent d’eux-mêmes par la synchronisation (onglet Intégrations).
        </div>
      </form>
      <p className="faible" style={{ fontSize: 12.5, margin: '0 0 8px' }}>{actifs} profil(s) actif(s) sur {liste.length}.</p>
      <div className="table-container">
        <table className="table-cartes">
          <thead>
            <tr><th>Nom</th><th>E-mail</th><th>Rôle</th><th>Origine</th><th className="num">Tâches ouvertes</th><th>Dernière connexion</th><th>État</th><th /></tr>
          </thead>
          <tbody>
            {liste.map((h) => (
              <tr key={h.id}>
                <td style={{ fontWeight: 550 }}>{nomComplet(h) || '—'}{h.id === moiId && <span className="faible" style={{ fontWeight: 400 }}> (vous)</span>}</td>
                <td className="mono">{h.email}</td>
                <td>{libelle(ROLES, h.role)}</td>
                <td><BadgeSource source={h.source} /></td>
                <td className="num">{h.taches_ouvertes}</td>
                <td>{h.derniere_connexion ? horodatage(h.derniere_connexion) : <span className="faible">jamais</span>}</td>
                <td><BadgeActif actif={h.actif} oui="Actif" non="Désactivé" /></td>
                <td><button type="button" className="btn btn-petit" onClick={() => setEdition(h)}>Modifier</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edition && (
        <ModaleProfil
          profil={edition}
          estMoi={edition.id === moiId}
          onFermer={() => setEdition(null)}
          onFait={(m) => { setEdition(null); setSucces(m); charger(); ref.recharger(); }}
        />
      )}
    </>
  );
}

function ModaleProfil({ profil, estMoi, onFermer, onFait }) {
  const crm = profil.source === 'crm';
  const [v, setV] = useState({ ...profil, prenom: profil.prenom || '', nom: profil.nom || '' });
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const maj = (p) => setV((x) => ({ ...x, ...p }));

  async function enregistrer() {
    setEnvoi(true); setErreur(null);
    try { await admin.modifierHabilitation(profil.id, v); onFait('Profil enregistré.'); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  }

  return (
    <Modale
      titre={`Profil — ${nomComplet(profil) || profil.email}`}
      onFermer={onFermer}
      actions={(
        <>
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button type="button" className="btn btn-primaire" onClick={enregistrer} disabled={envoi}>{envoi ? 'Enregistrement…' : 'Enregistrer'}</button>
        </>
      )}
    >
      {erreur && <Erreur>{erreur}</Erreur>}
      {crm && <div className="note-bandeau">Profil repris du CRM : le nom et l’e-mail y sont tenus, et un utilisateur désactivé dans le CRM l’est aussi ici à la synchronisation suivante.</div>}
      <div className="grille-champs">
        <div className="champ"><label htmlFor="mp-prenom">Prénom</label><input id="mp-prenom" value={v.prenom} disabled={crm} onChange={(e) => maj({ prenom: e.target.value })} /></div>
        <div className="champ"><label htmlFor="mp-nom">Nom</label><input id="mp-nom" value={v.nom} disabled={crm} onChange={(e) => maj({ nom: e.target.value })} /></div>
      </div>
      <div className="champ"><label htmlFor="mp-email">E-mail Microsoft</label><input id="mp-email" type="email" value={v.email} disabled={crm} onChange={(e) => maj({ email: e.target.value })} /></div>
      <div className="champ">
        <label htmlFor="mp-role">Rôle</label>
        <select id="mp-role" value={v.role} disabled={estMoi} onChange={(e) => maj({ role: e.target.value })}>
          {ROLES.map((r) => <option key={r.cle} value={r.cle}>{r.libelle} — {r.aide}</option>)}
        </select>
        {estMoi && <div className="aide">Vous ne pouvez pas retirer votre propre rôle d’administrateur.</div>}
      </div>
      <div className="champ">
        <label className="bascule"><input type="checkbox" checked={v.notif_email !== false} onChange={(e) => maj({ notif_email: e.target.checked })} />Notifications par e-mail</label>
        <label className="bascule"><input type="checkbox" checked={v.actif} disabled={estMoi} onChange={(e) => maj({ actif: e.target.checked })} />Actif (peut se connecter ; décocher retire l’accès immédiatement)</label>
      </div>
    </Modale>
  );
}

// ─── Intégrations : CRM, messagerie, Microsoft ───────────────────────────────
function Integrations() {
  const ref = useReferentiels();
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(() => admin.etat().then(setEtat).catch((e) => setErreur(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);

  async function synchroniser() {
    setEnvoi(true); setErreur(null); setSucces(null);
    try {
      const { bilan: b } = await admin.synchroCrm();
      setSucces(`Synchronisation terminée : ${b.utilisateurs_crees} profil(s) créé(s), ${b.utilisateurs_maj} mis à jour, ${b.utilisateurs_desactives} désactivé(s) ; ${b.concessions} concession(s) et ${b.clients} client(s) repris.`);
      ref.recharger();
    } catch (e) { setErreur(messageErreur(e)); }
    setEnvoi(false);
    charger();
  }

  if (!etat) return erreur ? <Erreur onReessayer={charger}>{erreur}</Erreur> : <Chargement />;
  const d = etat.crm.derniere;
  return (
    <>
      {erreur && <Erreur>{erreur}</Erreur>}
      {succes && <div className="succes-bandeau">{succes}</div>}
      <section className="carte" style={{ marginBottom: 'var(--esp-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 760 }}>
            <h2 className="carte-titre">Annuaire du CRM autodom97</h2>
            <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>
              Les utilisateurs, les concessions et les clients du CRM sont repris ici par l’API du CRM (jamais par sa base),
              au démarrage puis toutes les 6 heures. Un utilisateur du CRM devient un profil « Membre » ; s’il est désactivé dans le CRM,
              il l’est aussi ici.
            </p>
            <div style={{ fontSize: 13 }}>
              État : {etat.crm.configure
                ? <span className="badge badge-succes">Configurée</span>
                : <span className="badge badge-alerte">Non configurée</span>}
              {d && <> · dernière synchronisation {horodatage(d.le)} : {d.ok ? <span className="badge badge-succes badge-sans-point">réussie</span> : <span className="badge badge-erreur badge-sans-point" title={d.erreur}>échec — {d.erreur}</span>}</>}
            </div>
            {!etat.crm.configure && (
              <p className="faible" style={{ fontSize: 12.5, marginTop: 8 }}>
                À faire par l’administrateur du serveur : renseigner <span className="mono">CRM_API_URL</span> et <span className="mono">CRM_API_KEY</span> dans
                le <span className="mono">.env</span> du backend, avec la même valeur que A97_PROJETS_API_KEY dans le .env du CRM (voir la Documentation).
                En attendant, créez les profils, concessions et clients à la main.
              </p>
            )}
          </div>
          <button type="button" className="btn btn-primaire" onClick={synchroniser} disabled={!etat.crm.configure || envoi}>
            {envoi ? 'Synchronisation…' : 'Synchroniser maintenant'}
          </button>
        </div>
      </section>
      <div className="grille-2">
        <section className="carte">
          <h2 className="carte-titre">E-mails de notification</h2>
          <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>Envoyés par Brevo (comme le CRM) pour une assignation de tâche, une nomination de responsable ou une mention.</p>
          {etat.mail ? <span className="badge badge-succes">Configurés</span> : <span className="badge badge-alerte">Non configurés — seules les notifications dans l’application partent</span>}
        </section>
        <section className="carte">
          <h2 className="carte-titre">Connexion Microsoft</h2>
          <p style={{ margin: '0 0 8px', fontSize: 13.5 }}>Seul mode de connexion. Le droit d’entrée est le profil actif.</p>
          {etat.microsoft ? <span className="badge badge-succes">Configurée</span> : <span className="badge badge-alerte">En attente de l’application Entra (service informatique)</span>}
        </section>
      </div>
    </>
  );
}
